import React from "react";
import {
  DecimalNumberInput,
  WholeNumberInput,
  sanitizeWholeNumberInput,
} from "../../../../common/dashboard/CurrencyInput";
import { sanitizeCurrencyValue } from "../../../../common/dashboard/currencyUnits";
import { settingsInputClassName } from "./settingsStyles";

// Re-export shim (module 11 Phase A): the shared numeric-entry primitives were
// promoted to src/components/common/dashboard/CurrencyInput.tsx /
// currencyUnits.ts. Every pre-promotion export keeps its name and behavior so
// all ChamberSettings consumers compile unchanged. MoneyInput (legacy "$"
// embedded in the value string, CS-010 blur format) stays here until its
// consumers migrate to the promoted CurrencyInput (numeric-out contract).

export { DecimalNumberInput, WholeNumberInput, sanitizeWholeNumberInput };

// Promoted as sanitizeCurrencyValue; identical logic (digits + single dot,
// decimals clamped to 2).
export const sanitizeDecimalInput = sanitizeCurrencyValue;

export const sanitizeMoneyInput = (value: string): string => {
  const decimal = sanitizeDecimalInput(value);
  return decimal.length > 0 ? `$${decimal}` : "";
};

// POL-005 (B13 cheap fix): canonical money string is "$1234.00" — NO thousands
// commas — matching settingsMappers formatDollarAmount, which produces every
// dirty-state baseline. The previous toLocaleString("en-CA") grouping meant a
// blur of a >999 saved value ("$1,200.00" vs baseline "$1200.00") could
// phantom-dirty a section without any user edit. The full CurrencyInput
// (numeric-out) migration is deferred to a coordinated B13+B14 change.
export const formatMoneyValueForDisplay = (value: string): string => {
  const numeric = Number.parseFloat(value.replace(/[^0-9.]/g, ""));
  if (Number.isNaN(numeric)) {
    return "";
  }
  return `$${numeric.toFixed(2)}`;
};

type NumericInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel?: string;
};

export const MoneyInput = ({
  value,
  onChange,
  placeholder = "$0.00",
  className,
  disabled = false,
  readOnly = false,
  ariaLabel,
}: NumericInputProps) => (
  <input
    inputMode="decimal"
    value={value}
    onChange={(event) => onChange(sanitizeMoneyInput(event.target.value))}
    onBlur={() => {
      const formatted = formatMoneyValueForDisplay(value);
      if (formatted !== value) {
        onChange(formatted);
      }
    }}
    placeholder={placeholder}
    disabled={disabled}
    readOnly={readOnly}
    aria-label={ariaLabel}
    className={className ?? `${settingsInputClassName} w-full`}
  />
);
